import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Truck, Phone, MapPin, ChevronRight, Plus, Search, X } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, STATUS_META, STATUS_ORDER, PAYMENT_LABEL, fmtDate, todayStr } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const emptyManual = {
  customer_name: "", phone: "", address: "", order_type: "purchase",
  product_id: "", quantity: 1, delivery_date: todayStr(), payment_method: "cod", notes: "",
};

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => b.created_at.localeCompare(a.created_at) },
  oldest: { label: "Oldest first", fn: (a, b) => a.created_at.localeCompare(b.created_at) },
  delivery_asc: { label: "Delivery date ↑", fn: (a, b) => (a.delivery_date || "").localeCompare(b.delivery_date || "") },
  delivery_desc: { label: "Delivery date ↓", fn: (a, b) => (b.delivery_date || "").localeCompare(a.delivery_date || "") },
  name: { label: "Customer A–Z", fn: (a, b) => a.customer_name.localeCompare(b.customer_name) },
  amount_desc: { label: "Amount high → low", fn: (a, b) => b.total_amount - a.total_amount },
  amount_asc: { label: "Amount low → high", fn: (a, b) => a.total_amount - b.total_amount },
  status: { label: "Status", fn: (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) },
};

const digits = (s) => String(s || "").replace(/\D/g, "");

export const matchesQuery = (q, ...fields) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const nd = digits(needle);
  return fields.some((f) => {
    const v = String(f || "").toLowerCase();
    return v.includes(needle) || (nd.length >= 3 && digits(v).includes(nd));
  });
};

export const OrdersTab = ({ orders, products, reload }) => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(emptyManual);
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => orders
    .filter((o) => filter === "all" || o.status === filter)
    .filter((o) => !dateFrom || (o.delivery_date || "") >= dateFrom)
    .filter((o) => !dateTo || (o.delivery_date || "") <= dateTo)
    .filter((o) => matchesQuery(query, o.order_code, o.customer_name, o.phone, o.address, o.items.map((i) => i.product_name).join(" ")))
    .sort(SORTS[sort].fn), [orders, filter, query, sort, dateFrom, dateTo]);
  const hasFilters = query || dateFrom || dateTo || filter !== "all";
  const options = products.filter((p) => p.type === m.order_type);
  const chosen = products.find((p) => p.id === m.product_id);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Status updated");
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  const createManual = async () => {
    if (!m.customer_name || !m.phone || !m.address || !chosen) {
      toast.error("Fill name, phone, address and size");
      return;
    }
    setSaving(true);
    try {
      await api.post("/orders", {
        customer_name: m.customer_name, phone: m.phone, address: m.address,
        items: [{ product_id: chosen.id, product_name: chosen.name, size: chosen.size, order_type: chosen.type, quantity: Number(m.quantity), unit_price: chosen.price_jmd }],
        delivery_date: m.delivery_date, payment_method: m.payment_method, notes: m.notes, source: "phone",
      });
      toast.success("Phone order added");
      setOpen(false);
      setM(emptyManual);
      reload();
    } catch {
      toast.error("Could not add order");
    } finally {
      setSaving(false);
    }
  };

  const counts = STATUS_ORDER.reduce((a, s) => ({ ...a, [s]: orders.filter((o) => o.status === s).length }), {});

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="admin-orders-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, code, address…" className="h-10 w-72 pl-9 rounded-xl" />
            {query && <button onClick={() => setQuery("")} data-testid="admin-orders-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-orders-status-filter" className="w-48 h-10 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All orders ({orders.length})</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label} ({counts[s]})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="admin-orders-sort" className="w-44 h-10 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Input type="date" data-testid="admin-orders-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 w-36 rounded-xl" />
            <span>→</span>
            <Input type="date" data-testid="admin-orders-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 w-36 rounded-xl" />
          </div>
          {hasFilters && (
            <button data-testid="admin-orders-reset" onClick={() => { setQuery(""); setFilter("all"); setDateFrom(""); setDateTo(""); }} className="text-xs font-semibold text-sky-600 hover:underline">Reset</button>
          )}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-btn-add-manual-order" className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold">
              <Phone className="w-4 h-4 mr-1.5" /> New Phone Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Phone Order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="manual-order-name" value={m.customer_name} onChange={(e) => setM({ ...m, customer_name: e.target.value })} className="h-10 rounded-xl" /></div>
                <div><Label>Phone</Label><Input data-testid="manual-order-phone" value={m.phone} onChange={(e) => setM({ ...m, phone: e.target.value })} className="h-10 rounded-xl" /></div>
              </div>
              <div><Label>Address</Label><Input value={m.address} onChange={(e) => setM({ ...m, address: e.target.value })} className="h-10 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={m.order_type} onValueChange={(v) => setM({ ...m, order_type: v, product_id: "" })}>
                    <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="purchase">Purchase</SelectItem><SelectItem value="refill">Refill</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Size</Label>
                  <Select value={m.product_id} onValueChange={(v) => setM({ ...m, product_id: v })}>
                    <SelectTrigger data-testid="manual-order-size" className="h-10 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{options.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatJMD(p.price_jmd)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantity</Label><Input type="number" min={1} value={m.quantity} onChange={(e) => setM({ ...m, quantity: e.target.value })} className="h-10 rounded-xl" /></div>
                <div><Label>Delivery date</Label><Input type="date" value={m.delivery_date} onChange={(e) => setM({ ...m, delivery_date: e.target.value })} className="h-10 rounded-xl" /></div>
              </div>
              <div>
                <Label>Payment</Label>
                <Select value={m.payment_method} onValueChange={(v) => setM({ ...m, payment_method: v })}>
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cod">Cash on Delivery</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem></SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Notes" value={m.notes} onChange={(e) => setM({ ...m, notes: e.target.value })} className="rounded-xl" rows={2} />
            </div>
            <DialogFooter>
              <Button data-testid="manual-order-save" onClick={createManual} disabled={saving} className="rounded-xl bg-sky-600 text-white">
                <Plus className="w-4 h-4 mr-1" /> Add Order {chosen ? `· ${formatJMD(chosen.price_jmd * Number(m.quantity || 0))}` : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="text-xs text-slate-500 mb-3" data-testid="admin-orders-count">Showing {filtered.length} of {orders.length} orders</div>
      <div className="space-y-3" data-testid="admin-orders-list">
        {filtered.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`order-card-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-cw font-bold text-sky-700">{o.order_code}</span>
                  <Badge className={`border ${STATUS_META[o.status]?.cls}`}>{STATUS_META[o.status]?.label}</Badge>
                  {o.source === "phone" && <Badge variant="outline" className="text-xs"><Phone className="w-3 h-3 mr-1" />Phone</Badge>}
                </div>
                <div className="font-display font-bold text-slate-900 mt-1.5">{o.customer_name}</div>
                <div className="text-sm text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{o.phone}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{o.address}</span>
                </div>
                <div className="text-sm text-slate-600 mt-2">
                  {o.items.map((it, i) => <span key={i}>{it.quantity}× {it.product_name}{i < o.items.length - 1 ? ", " : ""}</span>)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-cw text-xl font-extrabold text-slate-900">{formatJMD(o.total_amount)}</div>
                <div className="text-xs text-slate-400">{PAYMENT_LABEL[o.payment_method]}</div>
                <div className="text-xs text-slate-400 mt-0.5">Deliver {fmtDate(o.delivery_date)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><ChevronRight className="w-3 h-3" />Update status</span>
              <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                <SelectTrigger data-testid={`order-status-select-${o.id}`} className="w-48 h-9 rounded-lg ml-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
            <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" /> No orders in this view.
          </div>
        )}
      </div>
    </div>
  );
};
